import React from 'react';
import 'twin.macro';

import Button from 'components/button/Button';
import { Modal } from 'components/page/app-page/Modal';
import { Title3 } from 'components/typography/Typography';
import { RefusalInfo } from 'hooks/useAutoSave';
import { hourAndMinute } from 'api/waitingChanges';

/**
 * D10, scene 14, 5b item 16: a save was refused because the thing was
 * changed elsewhere since this screen was opened. A blocking box; nothing
 * more is saved until the person has chosen.
 *
 * The words are the description's (5c). The colleague is named "Anna P."
 * and the button for one's own other tab reads "Load that version" (both
 * Steven, 4 Oct 2026).
 */
const OutOfDateBox: React.FC<{
  refusal: RefusalInfo | null;
  /** Theirs stays, mine is dropped; nothing is merged. */
  onLoadTheirs: () => void;
  /** My whole screen goes over theirs, knowingly. */
  onKeepMine: () => void;
}> = ({ refusal, onLoadTheirs, onKeepMine }) => {
  // Three cases: a colleague (named), the same person on another screen,
  // and a change sympl cannot put a name to (made by sympl itself, or an old
  // record). The third must not claim "you" (Steven, 4 Oct 2026).
  const byColleague = !!refusal && !refusal.own && !!refusal.by;
  const at = hourAndMinute(refusal?.at ?? null);
  const sentence = byColleague
    ? `${refusal?.by} changed this at ${at}.`
    : refusal?.own
    ? `You changed this at ${at} in another tab.`
    : `This was changed at ${at}.`;

  return (
    <Modal show={!!refusal} onClose={() => undefined} hideCloseButton>
      <div
        tw="flex flex-col gap-4 w-[32rem] max-w-[80vw] pr-10"
        data-testid="out-of-date-box"
      >
        <Title3>{sentence}</Title3>
        <div tw="flex gap-3 justify-end">
          <Button
            variant="secondary"
            testId="out-of-date-load-theirs"
            onClick={onLoadTheirs}
          >
            {byColleague
              ? `Load ${refusal?.by}'s version`
              : 'Load that version'}
          </Button>
          <Button testId="out-of-date-keep-mine" onClick={onKeepMine}>
            Keep mine
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default OutOfDateBox;
